/**
 * SuperAdmin Centre Insights - read-only numbers per centre, solo healer or the pool.
 * GET /api/superadmin/centre-insights/{options,summary,people,person/:id}
 * This page is rate limited by ipAuthLimiter only (100 requests per 15 minutes per IP, shared with other Super Admin
 * pages). Calls are kept few on purpose: options load once, the summary loads on centre/period change, people load
 * only for a chosen centre, and search is debounced. The requests list loads only while its section is open, and
 * makes one call per open, status chip, page or period change. The request detail panel and the Payouts section are in
 * CentreInsightsDrill.js; the "Patients paid" card and Payments section are in CentreInsightsPayments.js. Only one of the
 * Requests, Payouts and Payments sections is open at a time.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiRefreshCw, FiX } from 'react-icons/fi';
import api from '../../utils/api';
import colors from '../../styles/colors';
import { RequestPanel, PayoutsSection } from './CentreInsightsDrill';
import { PatientsPaidCard, PaymentsSection, usePaymentTotals } from './CentreInsightsPayments';
import { HealersCard, PatientsCard, PeopleFilterPill, statusFits } from './CentreInsightsPeopleFilter';
import { ConcernsCard, ConcernsSection, ConcernPanel } from './CentreInsightsConcerns';

const BASE = '/superadmin/centre-insights';
const PERIODS = [['all', 'All time'], ['month', 'This month'], ['last30', 'Last 30 days']];
const ROLES = [['all', 'All'], ['centre_admin', 'Centre admins'], ['healer', 'Healers'], ['solo_healer', 'Solo healers'],
  ['pool_healer', 'Pool healers'], ['patient', 'Patients']];
const NOT_YET = 'Not included yet';

// Always two decimals with Indian grouping: 2.6 -> ₹2.60, 1250.5 -> ₹1,250.50.
const money = n => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const day = d => (d ? new Date(d).toLocaleDateString('en-IN') : '-');
const errText = e => (e?.response?.status === 429
  ? 'Too many requests. Please wait a few minutes and try again.'
  : e?.response?.data?.message || 'Could not load. Please try again.');

const S = {
  wrap: { padding: '0 0 24px', color: colors.textPrimary },
  row: { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' },
  chip: on => ({ minHeight: '44px', padding: '0 14px', borderRadius: '22px', fontSize: '14px', cursor: 'pointer',
    border: `1px solid ${on ? colors.brandPrimaryDark : colors.borderDefault}`,
    background: on ? colors.brandPrimaryLight : '#fff', color: colors.textPrimary, fontWeight: on ? 600 : 400 }),
  input: { minHeight: '44px', padding: '0 12px', fontSize: '16px', borderRadius: '8px',
    border: `1px solid ${colors.borderDefault}`, background: '#fff', maxWidth: '100%', boxSizing: 'border-box' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px', marginBottom: '16px' },
  card: { background: '#fff', border: `1px solid ${colors.borderDefault}`, borderRadius: '10px', padding: '14px 16px' },
  cardTitle: { fontSize: '13px', fontWeight: 600, color: colors.textSecondary, marginBottom: '8px' },
  line: { display: 'flex', justifyContent: 'space-between', gap: '8px', fontSize: '14px', padding: '3px 0' },
  note: { fontSize: '12px', color: colors.textSecondary, marginTop: '6px' },
  scroll: { overflowX: 'auto', background: '#fff', border: `1px solid ${colors.borderDefault}`, borderRadius: '10px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px', minWidth: '640px' },
  th: { textAlign: 'left', padding: '10px 12px', fontSize: '12px', color: colors.textSecondary, background: colors.backgroundSecondary, whiteSpace: 'nowrap' },
  td: { padding: '8px 12px', borderTop: `1px solid ${colors.borderDefault}`, verticalAlign: 'middle' },
  btn: { minHeight: '44px', minWidth: '44px', padding: '0 14px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer',
    border: `1px solid ${colors.borderDefault}`, background: '#fff', color: colors.textPrimary },
  nameBtn: { minHeight: '44px', background: 'none', border: 'none', padding: 0, fontSize: '14px', fontWeight: 600,
    color: colors.statusInfo, cursor: 'pointer', textAlign: 'left', textDecoration: 'underline' },
  error: { background: colors.statusDangerLight, color: colors.statusDanger, borderRadius: '8px', padding: '12px', marginBottom: '12px', fontSize: '14px' },
  linkLine: { display: 'flex', justifyContent: 'space-between', gap: '8px', width: '100%', minHeight: '44px', alignItems: 'center',
    background: 'none', border: 'none', borderBottom: `1px solid ${colors.borderDefault}`, padding: '0 2px', fontSize: '14px',
    color: colors.statusInfo, cursor: 'pointer', textAlign: 'left' },
  tdWrap: { padding: '8px 12px', borderTop: `1px solid ${colors.borderDefault}`, verticalAlign: 'top', maxWidth: '280px',
    minWidth: '200px', whiteSpace: 'normal', wordBreak: 'break-word' },
  panel: { position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(420px, 100%)', background: '#fff', zIndex: 1000,
    boxShadow: '-4px 0 16px rgba(0,0,0,0.15)', padding: '16px', overflowY: 'auto', boxSizing: 'border-box' },
};

const Line = ({ label, value }) => <div style={S.line}><span>{label}</span><strong>{value}</strong></div>;
const Card = ({ title, children, note }) => (
  <div style={S.card}><div style={S.cardTitle}>{title}</div>{children}{note && <div style={S.note}>{note}</div>}</div>
);
const Unavailable = () => <div style={S.note}>Unavailable right now</div>;
const LinkLine = ({ label, value, onClick, what = 'requests' }) => (
  <button type="button" style={S.linkLine} onClick={onClick} aria-label={`Show ${label} ${what}`}>
    <span>{label}</span><strong>{value}</strong>
  </button>
);
const withSource = d => `${d.phone}${d.phoneSource === 'whatsapp' ? ' (WhatsApp)' : ''}`;
const REQUEST_CHIPS = [['all', 'All'], ['new', 'New'], ['assigned', 'Assigned'], ['in_progress', 'In progress'],
  ['completed', 'Completed'], ['cancelled', 'Cancelled']];
const words = v => String(v || '-').replace(/_/g, ' ');
// Shared with CentreInsightsDrill.js, passed as a prop so that file does not import this one.
const UI = { S, money, day, errText, Line, LinkLine, Card };

function Cards({ b, single, excludesSolo, onRequests, onPayouts, payTotals, onPayments, onPeople, onConcerns }) {
  const { healers, patients, requests, feedback, payouts, concerns } = b;
  const solo = payouts && payouts.supported === false;
  return (
    <div style={S.grid}>
      <HealersCard ui={UI} healers={healers} kind={b.kind} onPeople={onPeople} />
      <PatientsCard ui={UI} patients={patients} note="Active = has an open request, or a request in the last 30 days." onPeople={onPeople} />
      <Card title="Requests received">{requests ? (requests.received === 0 && single
        ? <div style={S.note}>No requests yet at this centre</div>
        : <>{[['Received', 'all', requests.received], ['New', 'new', requests.new], ['Assigned', 'assigned', requests.assigned],
          ['In progress', 'in_progress', requests.inProgress], ['Completed', 'completed', requests.completed],
          ['Cancelled', 'cancelled', requests.cancelled]].map(([label, status, n]) => (onRequests
          ? <LinkLine key={label} label={label} value={n} onClick={() => onRequests(status)} />
          : <Line key={label} label={label} value={n} />))}
          <Line label="Walk-ins" value={requests.walkIns} /></>) : <Unavailable />}</Card>
      <Card title="Feedback">{feedback ? (feedback.count === 0
        ? <div style={S.note}>No ratings yet</div>
        : <><Line label="Average (out of 5)" value={feedback.average} /><Line label="Ratings" value={feedback.count} /></>) : <Unavailable />}</Card>
      <Card title="Payouts" note={solo ? null : 'Ready to pay and On hold: current balance, not filtered by period.'}>
        {solo ? <div style={S.note}>{NOT_YET}</div> : payouts ? <>
          {[['Paid', 'paid', payouts.paid, payouts.paidLines], ['Ready to pay', 'ready', payouts.ready, payouts.readyLines],
            ['On hold', 'on_hold', payouts.onHold, payouts.onHoldLines]].map(([label, status, amount, n]) => (onPayouts
            ? <LinkLine key={label} label={label} what="payouts" value={`${money(amount)} (${n})`} onClick={() => onPayouts(status)} />
            : <Line key={label} label={label} value={`${money(amount)} (${n})`} />))}
          {excludesSolo && <div style={S.note}>Excludes solo healers</div>}</> : <Unavailable />}
      </Card>
      {onPayments && <PatientsPaidCard ui={UI} totals={payTotals} onOpen={onPayments} />}
      <ConcernsCard ui={UI} concerns={concerns} solo={solo} excludesSolo={excludesSolo} notYet={NOT_YET} onOpen={onConcerns} />
    </div>
  );
}

function AllTable({ rows, totals, onPick }) {
  const cell = (v, supported) => (supported === false ? NOT_YET : v ?? '-');
  return (
    <div style={S.scroll}><table style={{ ...S.table, minWidth: '880px' }}>
      <thead><tr>{['Centre', 'Type', 'Healers (active)', 'Patients (active)', 'Requests', 'Completed', 'Paid', 'On hold', 'Open concerns', 'Rating']
        .map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.tenantId}>
            <td style={S.td}><button type="button" style={S.nameBtn} onClick={() => onPick(r.tenantId)}>{r.name}</button>{r.city ? ` (${r.city})` : ''}</td><td style={S.td}>{r.kind}</td>
            <td style={S.td}>{r.healers ? `${r.healers.total} (${r.healers.active})` : '-'}</td>
            <td style={S.td}>{r.patients ? `${r.patients.registered} (${r.patients.active ?? '-'})` : '-'}</td>
            <td style={S.td}>{r.requests ? r.requests.received : '-'}</td><td style={S.td}>{r.requests ? r.requests.completed : '-'}</td>
            <td style={S.td}>{cell(r.payouts && money(r.payouts.paid), r.payouts?.supported)}</td>
            <td style={S.td}>{cell(r.payouts && money(r.payouts.onHold), r.payouts?.supported)}</td>
            <td style={S.td}>{cell(r.concerns?.open, r.concerns?.supported)}</td>
            <td style={S.td}>{r.feedback ? (r.feedback.count ? r.feedback.average : 'No ratings yet') : '-'}</td>
          </tr>
        ))}
        {totals && (
          <tr style={{ fontWeight: 700, background: colors.backgroundSecondary }}>
            <td style={S.td}>Totals</td><td style={S.td} />
            <td style={S.td}>{totals.healers ? `${totals.healers.total} (${totals.healers.active})` : '-'}</td>
            <td style={S.td}>{totals.patients ? `${totals.patients.registered} (${totals.patients.active})` : '-'}</td>
            <td style={S.td}>{totals.requests?.received ?? '-'}</td><td style={S.td}>{totals.requests?.completed ?? '-'}</td>
            <td style={S.td}>{totals.payouts ? <>{money(totals.payouts.paid)}<div style={S.note}>Excludes solo healers</div></> : '-'}</td>
            <td style={S.td}>{totals.payouts ? <>{money(totals.payouts.onHold)}<div style={S.note}>Excludes solo healers</div></> : '-'}</td>
            <td style={S.td}>{totals.concerns ? <>{totals.concerns.open}<div style={S.note}>Excludes solo healers</div></> : '-'}</td>
            <td style={S.td}>{totals.feedback ? (totals.feedback.count ? totals.feedback.average : 'No ratings yet') : '-'}</td>
          </tr>
        )}
      </tbody>
    </table></div>
  );
}

function RequestsSection({ state, status, page, onStatus, onPage, onClose, onRetry, onOpen }) {
  const { loading, error, rows, meta } = state;
  return (
    <div style={{ marginTop: '16px' }}>
      <div style={{ ...S.row, justifyContent: 'space-between' }}>
        <h3 style={{ fontSize: '16px', margin: 0 }}>Requests</h3>
        <button type="button" style={S.btn} onClick={onClose}>Close</button>
      </div>
      <div style={S.row}>
        {REQUEST_CHIPS.map(([v, l]) => <button key={v} type="button" style={S.chip(status === v)} onClick={() => onStatus(v)}>{l}</button>)}
      </div>
      {error && <div style={S.error}>{error} <button type="button" style={S.btn} onClick={onRetry}>Retry</button></div>}
      {loading && <p>Loading...</p>}
      {!loading && !error && rows.length === 0 && <p style={S.note}>No requests for this filter</p>}
      {rows.length > 0 && <div style={S.scroll}><table style={{ ...S.table, minWidth: '960px' }}>
        <thead><tr>{['Date', 'Patient', 'Type', 'Phone', 'Condition', 'Healer', 'Status', 'Source'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
        <tbody>{rows.map(r => (
          <tr key={r.id}>
            <td style={S.td}>{day(r.createdAt)}</td>
            <td style={S.td}><button type="button" style={S.nameBtn} onClick={() => onOpen(r.id)}>{r.patientName || 'Unknown patient'}</button></td>
            <td style={S.td}>{r.patientType === 'walk_in' ? 'Walk-in' : 'Registered'}</td><td style={S.td}>{r.patientPhoneMasked || '-'}</td>
            <td style={S.tdWrap}>{r.condition || '-'}</td><td style={S.td}>{r.healerName || 'Not assigned'}</td>
            <td style={S.td}>{words(r.status)}</td><td style={S.td}>{words(r.source)}</td>
          </tr>))}</tbody>
      </table></div>}
      <div style={S.note}>Requests list is recorded in the audit log.</div>
      <div style={S.note}>Condition is shown as typed by the patient or healer.</div>
      {meta && meta.totalPages > 1 && <div style={{ ...S.row, marginTop: '10px' }}>
        <button type="button" style={S.btn} disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</button>
        <span style={{ fontSize: '14px' }}>Page {meta.page} of {meta.totalPages}</span>
        <button type="button" style={S.btn} disabled={page >= meta.totalPages} onClick={() => onPage(page + 1)}>Next</button>
      </div>}
    </div>
  );
}

function DetailPanel({ state, onClose }) {
  const d = state.data;
  return (
    <div style={S.panel} role="dialog" aria-label="Person details">
      <div style={{ ...S.row, justifyContent: 'space-between' }}>
        <strong style={{ fontSize: '16px' }}>{d ? d.name : 'Person details'}</strong>
        <button type="button" style={S.btn} onClick={onClose} aria-label="Close"><FiX /></button>
      </div>
      <div style={S.note}>Opening a person is recorded in the audit log.</div>
      {state.loading && <p>Loading...</p>}
      {state.error && <div style={S.error}>{state.error}</div>}
      {d && <div style={{ marginTop: '12px' }}>
        <Line label="Role" value={d.role} /><Line label="Status" value={d.status} /><Line label="Phone" value={d.phone ? withSource(d) : '-'} />
        <Line label="City" value={d.city || '-'} /><Line label="Joined" value={day(d.joined)} />
        {d.requestsHandled !== undefined && <>
          <Line label="Requests handled" value={d.requestsHandled} /><Line label="Completed" value={d.completed} />
          <Line label="Average rating" value={d.ratingCount ? `${d.averageRating} (${d.ratingCount})` : 'No ratings yet'} />
          {d.payouts?.supported === false ? <Line label="Payouts" value={NOT_YET} /> : <>
            <Line label="Paid" value={money(d.payouts?.paid)} /><Line label="Ready to pay" value={money(d.payouts?.ready)} />
            <Line label="On hold" value={money(d.payouts?.onHold)} /><Line label="Open concerns" value={d.concernsOpen ?? 0} /></>}
        </>}
        {d.totalCases !== undefined && <>
          <Line label="Total cases" value={d.totalCases} /><Line label="Open cases" value={d.openCases} />
          <Line label="Last request" value={day(d.lastRequestDate)} /></>}
      </div>}
    </div>
  );
}

export default function CentreInsights() {
  const [options, setOptions] = useState([]);
  const [optError, setOptError] = useState('');
  const [tenantId, setTenantId] = useState('all');
  const [period, setPeriod] = useState('all');
  const [summary, setSummary] = useState({ loading: true, error: '', data: null });
  const [role, setRole] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [peopleStatus, setPeopleStatus] = useState(''); // '' = no status filter; only sent when it fits the role
  const [people, setPeople] = useState({ loading: false, error: '', rows: [], meta: null });
  const [detail, setDetail] = useState(null);
  const [reqOpen, setReqOpen] = useState(false);
  const [reqStatus, setReqStatus] = useState('all');
  const [reqPage, setReqPage] = useState(1);
  const [reqs, setReqs] = useState({ loading: false, error: '', rows: [], meta: null });
  const [pay, setPay] = useState(null); // { status, key } while the Payouts section is open
  const [reqDetail, setReqDetail] = useState(null); // request id while the request panel is open
  const [pays, setPays] = useState(null); // { type, key } while the Payments section is open
  const [conc, setConc] = useState(null); // { state, key } while the Concerns section is open
  const [concernId, setConcernId] = useState(null); // concern id while the concern panel is open
  const seq = useRef({ summary: 0, people: 0, person: 0, requests: 0 });
  const peopleTop = useRef(null); // the People heading, scrolled into view by a card link
  const payTotals = usePaymentTotals(tenantId, period, errText); // separate non-blocking call; idle for All centres

  const loadOptions = useCallback(async () => {
    try { setOptError(''); const res = await api.get(`${BASE}/options`); setOptions(res.data.data.options); }
    catch (e) { setOptError(errText(e)); }
  }, []);
  const loadSummary = useCallback(async () => {
    const mine = ++seq.current.summary;
    setSummary(s => ({ ...s, loading: true, error: '' }));
    try {
      const res = await api.get(`${BASE}/summary`, { params: { tenantId, period } });
      if (mine === seq.current.summary) setSummary({ loading: false, error: '', data: res.data.data });
    } catch (e) { if (mine === seq.current.summary) setSummary({ loading: false, error: errText(e), data: null }); }
  }, [tenantId, period]);
  const loadPeople = useCallback(async () => {
    const mine = ++seq.current.people;
    setPeople(p => ({ ...p, loading: true, error: '' }));
    try {
      const params = { tenantId, role, page, ...(search ? { search } : {}), ...(statusFits(role, peopleStatus) ? { status: peopleStatus } : {}) };
      const res = await api.get(`${BASE}/people`, { params });
      if (mine === seq.current.people) setPeople({ loading: false, error: '', rows: res.data.data, meta: res.data.meta });
    } catch (e) { if (mine === seq.current.people) setPeople({ loading: false, error: errText(e), rows: [], meta: null }); }
  }, [tenantId, role, page, search, peopleStatus]);

  const loadRequests = useCallback(async () => {
    const mine = ++seq.current.requests;
    setReqs(r => ({ ...r, loading: true, error: '' }));
    try {
      const res = await api.get(`${BASE}/requests`, { params: { tenantId, period, status: reqStatus, page: reqPage } });
      if (mine === seq.current.requests) setReqs({ loading: false, error: '', rows: res.data.data, meta: res.data.meta });
    } catch (e) { if (mine === seq.current.requests) setReqs({ loading: false, error: errText(e), rows: [], meta: null }); }
  }, [tenantId, period, reqStatus, reqPage]);

  useEffect(() => { loadOptions(); }, [loadOptions]);
  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { if (tenantId !== 'all') loadPeople(); }, [tenantId, loadPeople]);
  // Fetches only while the section is open: one call per open, status chip, page or period change.
  useEffect(() => { if (reqOpen && tenantId !== 'all') loadRequests(); }, [reqOpen, tenantId, loadRequests]);
  // Debounce: one people call after typing stops. Under 2 characters means no search filter.
  useEffect(() => {
    const t = setTimeout(() => { const v = searchText.trim(); setSearch(v.length >= 2 ? v : ''); setPage(1); }, 500);
    return () => clearTimeout(t);
  }, [searchText]);

  const pickTenant = (v) => {
    seq.current.requests++; setReqOpen(false); setReqStatus('all'); setReqPage(1); setPay(null); setPays(null); setReqDetail(null); setConc(null); setConcernId(null);
    setTenantId(v); setRole('all'); setPeopleStatus(''); setPage(1); setSearchText(''); setSearch(''); setDetail(null);
  };
  const openRequests = (status) => { setPay(null); setPays(null); setConc(null); setReqStatus(status); setReqPage(1); setReqOpen(true); };
  const pickReqStatus = (status) => { setReqStatus(status); setReqPage(1); };
  const closeRequests = () => { seq.current.requests++; setReqOpen(false); };
  // One drill-down at a time: opening Payouts closes Requests. The key restarts the section when another line is clicked.
  const openPayouts = (status) => { seq.current.requests++; setReqOpen(false); setPays(null); setConc(null); setPay(p => ({ status, key: (p?.key || 0) + 1 })); };
  const openPayments = (type) => { seq.current.requests++; setReqOpen(false); setPay(null); setConc(null); setPays(p => ({ type, key: (p?.key || 0) + 1 })); };
  const openConcerns = (st) => { seq.current.requests++; setReqOpen(false); setPay(null); setPays(null); setConc(c => ({ state: st, key: (c?.key || 0) + 1 })); };
  // The concern, request and person panels close each other.
  const openConcern = (id) => { seq.current.person++; setDetail(null); setReqDetail(null); setConcernId(id); };
  const openRequest = (id) => { seq.current.person++; setDetail(null); setConcernId(null); setReqDetail(id); };
  const pickRole = (v) => { setRole(v); setPeopleStatus(''); setPage(1); };
  // Card links: set role and status together (page 1) and clear the search so the list matches the card; close nothing else; scroll to People.
  const openPeople = (r, st) => {
    setRole(r); setPeopleStatus(st); setPage(1); setSearchText(''); setSearch('');
    if (peopleTop.current && peopleTop.current.scrollIntoView) peopleTop.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const openPerson = async (id) => {
    const mine = ++seq.current.person;
    setReqDetail(null); setConcernId(null);
    setDetail({ loading: true, error: '', data: null });
    try {
      const res = await api.get(`${BASE}/person/${id}`, { params: { tenantId } });
      if (mine === seq.current.person) setDetail({ loading: false, error: '', data: res.data.data });
    } catch (e) { if (mine === seq.current.person) setDetail({ loading: false, error: errText(e), data: null }); }
  };

  const data = summary.data;
  const single = tenantId !== 'all';
  const block = single ? data?.rows?.[0] : data?.totals;
  const groups = [['centre', 'Centres'], ['solo', 'Solo healers'], ['pool', 'Pool']];

  return (
    <div style={S.wrap}>
      <h2 style={{ fontSize: '20px', margin: '0 0 12px' }}>Centre Insights</h2>
      <div style={S.row}>
        <select style={{ ...S.input, minWidth: '220px' }} value={tenantId} onChange={e => pickTenant(e.target.value)} aria-label="Centre">
          <option value="all">All centres</option>
          {groups.map(([kind, label]) => {
            const list = options.filter(o => o.kind === kind);
            return list.length ? <optgroup key={kind} label={label}>
              {list.map(o => <option key={o.id} value={o.id}>{o.name}{o.city ? ` (${o.city})` : ''}</option>)}</optgroup> : null;
          })}
        </select>
        <button type="button" style={S.btn} onClick={() => { loadOptions(); loadSummary(); if (single) loadPeople(); }} aria-label="Refresh">
          <FiRefreshCw />
        </button>
      </div>
      <div style={S.row}>
        {PERIODS.map(([v, l]) => <button key={v} type="button" style={S.chip(period === v)} onClick={() => { setPeriod(v); setReqPage(1); }}>{l}</button>)}
      </div>
      {optError && <div style={S.error}>{optError} <button type="button" style={S.btn} onClick={loadOptions}>Retry</button></div>}

      {summary.loading && !data && <p>Loading...</p>}
      {summary.error && <div style={S.error}>{summary.error} <button type="button" style={S.btn} onClick={loadSummary}>Retry</button></div>}
      {data?.partial && <div style={S.error}>Some numbers could not be loaded. The rest are shown.</div>}
      {block && <Cards b={block} single={single} excludesSolo={!single} onRequests={single ? openRequests : null} onPayouts={single ? openPayouts : null}
        payTotals={payTotals} onPayments={single ? openPayments : null}
        onPeople={single && block.tenantId === tenantId ? openPeople : null}
        onConcerns={single && block.tenantId === tenantId ? openConcerns : null} />}
      {!single && data && <AllTable rows={data.rows} totals={data.totals} onPick={pickTenant} />}

      {single && <>
        {pay && <PayoutsSection key={pay.key} ui={UI} tenantId={tenantId} period={period} initialStatus={pay.status}
          onClose={() => setPay(null)} />}
        {pays && <PaymentsSection key={pays.key} ui={UI} tenantId={tenantId} period={period} initialType={pays.type}
          onClose={() => setPays(null)} onOpenRequest={openRequest} />}
        {conc && <ConcernsSection key={conc.key} ui={UI} tenantId={tenantId} period={period} initialState={conc.state}
          onClose={() => setConc(null)} onOpenConcern={openConcern} />}
        {reqOpen && <RequestsSection state={reqs} status={reqStatus} page={reqPage} onStatus={pickReqStatus} onPage={setReqPage}
          onClose={closeRequests} onRetry={loadRequests} onOpen={openRequest} />}
        <h3 ref={peopleTop} style={{ fontSize: '16px', margin: '20px 0 8px' }}>People</h3>
        <div style={S.row}>
          {ROLES.map(([v, l]) => <button key={v} type="button" style={S.chip(role === v)} onClick={() => pickRole(v)}>{l}</button>)}
        </div>
        <PeopleFilterPill ui={UI} role={role} status={peopleStatus} onClear={() => { setPeopleStatus(''); setPage(1); }} />
        <div style={S.row}>
          <input style={{ ...S.input, minWidth: '240px' }} value={searchText} onChange={e => setSearchText(e.target.value)}
            placeholder="Search by name (2+ letters)" aria-label="Search people" />
        </div>
        {people.error && <div style={S.error}>{people.error} <button type="button" style={S.btn} onClick={loadPeople}>Retry</button></div>}
        {people.loading && <p>Loading...</p>}
        {!people.loading && !people.error && people.rows.length === 0 && <p style={S.note}>No people found.</p>}
        {people.rows.length > 0 && <div style={S.scroll}><table style={S.table}>
          <thead><tr>{['Name', 'Role', 'Status', 'Location', 'Joined', 'Phone'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>{people.rows.map(r => (
            <tr key={r.id}>
              <td style={S.td}><button type="button" style={S.nameBtn} onClick={() => openPerson(r.id)}>{r.name}</button></td>
              <td style={S.td}>{r.role}</td><td style={S.td}>{r.status}</td><td style={S.td}>{r.city || '-'}</td>
              <td style={S.td}>{day(r.joined)}</td><td style={S.td}>{r.phone ? withSource(r) : r.phoneMasked || '-'}</td>
            </tr>))}</tbody>
        </table></div>}
        <div style={S.note}>Healer and admin numbers shown here are recorded in the audit log.</div>
        {people.meta && people.meta.totalPages > 1 && <div style={{ ...S.row, marginTop: '10px' }}>
          <button type="button" style={S.btn} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
          <span style={{ fontSize: '14px' }}>Page {people.meta.page} of {people.meta.totalPages}</span>
          <button type="button" style={S.btn} disabled={page >= people.meta.totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
        </div>}
      </>}
      {detail && <DetailPanel state={detail} onClose={() => setDetail(null)} />}
      {concernId && single && <ConcernPanel ui={UI} tenantId={tenantId} concernId={concernId} onClose={() => setConcernId(null)} onViewRequest={openRequest} />}
      {reqDetail && single && <RequestPanel ui={UI} tenantId={tenantId} requestId={reqDetail} onClose={() => setReqDetail(null)} />}
    </div>
  );
}
